import React from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  Typography,
  useTheme
} from '@mui/material';
import {
  CloudUpload as SelectIcon,
  Edit as EditIcon,
  CheckCircle as ConfirmIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

interface ImportStepIndicatorProps {
  activeStep: number;
  completedSteps: number[];
  isInAIAvatarLayout?: boolean;
}

const ColorlibConnector = styled(StepConnector)<{ isInAIAvatarLayout?: boolean }>(({ theme, isInAIAvatarLayout }) => {
  const activeColor = isInAIAvatarLayout
    ? '#000000' // Always black in AI Avatar Layout for both themes
    : '#0F2A52';
  const gradient = isInAIAvatarLayout
    ? `linear-gradient(90deg, ${activeColor} 0%, ${activeColor} 100%)`
    : 'linear-gradient(90deg, #0F2A52 0%, #1976d2 100%)';
  
  return {
    '& .MuiStepConnector-line': {
      height: 2,
      border: 0,
      backgroundColor: isInAIAvatarLayout && theme.palette.mode === 'dark'
        ? '#808080' // Grey lines in dark theme
        : theme.palette.divider,
      borderRadius: 1,
    },
    '&.Mui-active .MuiStepConnector-line': {
      backgroundImage: gradient,
    },
    '&.Mui-completed .MuiStepConnector-line': {
      backgroundImage: gradient,
    },
  };
});

const ColorlibStepIconRoot = styled('div')<{
  ownerState: { completed?: boolean; active?: boolean };
  isInAIAvatarLayout?: boolean;
}>(({ theme, ownerState, isInAIAvatarLayout }) => {
  const activeColor = isInAIAvatarLayout
    ? '#000000' // Always black in AI Avatar Layout for both themes
    : '#0F2A52';
  const gradient = isInAIAvatarLayout
    ? `linear-gradient(136deg, ${activeColor} 0%, ${activeColor} 100%)`
    : 'linear-gradient(136deg, #0F2A52 0%, #1976d2 100%)';
  
  return {
    backgroundColor: theme.palette.grey[300],
    zIndex: 1,
    color: '#fff',
    width: 32,
    height: 32,
    display: 'flex',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '1rem',
    border: isInAIAvatarLayout && theme.palette.mode === 'dark' && !ownerState.active && !ownerState.completed
      ? '1px solid #ffffff' // White border in dark theme for inactive steps
      : 'none',
    ...(ownerState.active && {
      backgroundImage: gradient,
      boxShadow: '0 2px 6px 0 rgba(0,0,0,.2)',
    }),
    ...(ownerState.completed && {
      backgroundImage: gradient,
    }),
  };
});

const createColorlibStepIcon = (isInAIAvatarLayout: boolean) => {
  return function ColorlibStepIcon(props: any) {
    const { active, completed, className } = props;

    const icons: { [index: string]: React.ReactElement } = {
      1: <SelectIcon />,
      2: <EditIcon />,
      3: <ConfirmIcon />,
    };

    return (
      <ColorlibStepIconRoot 
        ownerState={{ completed, active }} 
        className={className}
        isInAIAvatarLayout={isInAIAvatarLayout}
      >
        {icons[String(props.icon)]}
      </ColorlibStepIconRoot>
    );
  };
};

const steps = [
  {
    label: 'Select File',
    description: 'Upload ZIP file to import'
  },
  {
    label: 'Edit Data',
    description: 'Review and edit comparison results'
  },
  {
    label: 'Confirm Import',
    description: 'Final confirmation and import'
  }
];

const ImportStepIndicator: React.FC<ImportStepIndicatorProps> = ({
  activeStep,
  completedSteps,
  isInAIAvatarLayout = false
}) => {
  const theme = useTheme();
  
  const primaryColor = isInAIAvatarLayout
    ? (theme.palette.mode === 'dark' ? '#ffffff' : '#000000') // White in dark theme, black in light theme
    : '#0F2A52';

  // Create step icon component with closure to capture isInAIAvatarLayout
  const StepIconComponent = createColorlibStepIcon(isInAIAvatarLayout);

  return (
    <Box sx={{ 
      width: '100%',
      mb: 2,
      p: 1.5,
      backgroundColor: 'rgba(15, 42, 82, 0.02)',
      borderRadius: 1,
      border: isInAIAvatarLayout && theme.palette.mode === 'dark'
        ? '1px solid #ffffff' // White border in dark theme
        : '1px solid rgba(15, 40, 76, 0.08)'
    }}>
      <Typography 
        variant="subtitle1" 
        sx={{ 
          color: primaryColor,
          fontWeight: 600,
          mb: 1.5,
          textAlign: 'center',
          fontSize: '0.9rem'
        }}
      >
        Import Process
      </Typography>
      
      <Stepper 
        alternativeLabel 
        activeStep={activeStep} 
        connector={<ColorlibConnector isInAIAvatarLayout={isInAIAvatarLayout} />}
        sx={{
          '& .MuiStepLabel-root': {
            '& .MuiStepLabel-label': {
              color: primaryColor,
              fontWeight: 500,
              fontSize: '0.75rem',
              '&.Mui-active': {
                color: primaryColor,
                fontWeight: 600,
              },
              '&.Mui-completed': {
                color: primaryColor,
                fontWeight: 600,
              },
            },
            '& .MuiStepLabel-labelContainer': {
              '& .MuiStepLabel-iconContainer': {
                '& .MuiStepLabel-iconContainer-text': {
                  fill: 'transparent',
                },
              },
            },
          },
        }}
      >
        {steps.map((step, index) => (
          <Step key={step.label}>
            <StepLabel
              StepIconComponent={StepIconComponent}
              StepIconProps={{
                icon: index + 1,
                completed: completedSteps.includes(index),
                active: activeStep === index,
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.25, fontSize: '0.75rem' }}>
                  {step.label}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  {step.description}
                </Typography>
              </Box>
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
};

export default ImportStepIndicator;
